import React, { useEffect, useRef, useState } from 'react';
import { UserAgentApplication } from 'msal';

import { getGoogleSettings, getMicrosoftSettings } from 'services/requests';
import { createHandoff, validateReturnUrl } from 'services/clientPortalSso';
import {
  IPendingRedirectSignIn,
  IRedirectSignInRequest,
  Provider,
  SignInErrorKind,
  buildGoogleAuthorizeUrl,
  buildPortalReturnUrl,
  clearPendingSignIn,
  confirmReturnUrl,
  parseGoogleRedirect,
  parseRedirectSignInRequest,
  readPendingSignIn,
  resolveClientPortalApiUrls,
  savePendingSignIn,
  signInRedirectUri,
  toSignInErrorKind,
} from 'signIn/redirectSignIn';

import './RedirectSignIn.css';

/** How long to wait for MSAL to report a redirect response before treating the visit as abandoned. */
const MICROSOFT_RESPONSE_TIMEOUT_MS = 10000;

const createMsal = async (): Promise<UserAgentApplication> => {
  const settings = await getMicrosoftSettings();
  return new UserAgentApplication({
    auth: {
      clientId: settings.auth.clientId,
      authority: settings.auth.authority,
      redirectUri: signInRedirectUri(window.location.href),
      // Handle the response right here instead of bouncing back to the start URL, which still carries
      // the portal's query string and would begin a second sign-in.
      navigateToLoginRequestUrl: false,
    },
  });
};

const backToPortal = (pending: IPendingRedirectSignIn, error: SignInErrorKind): void => {
  clearPendingSignIn();
  window.location.replace(buildPortalReturnUrl(pending.returnUrl, { error }, pending.state));
};

const handOff = async (
  pending: IPendingRedirectSignIn,
  token: string,
  tokenType: Provider,
): Promise<void> => {
  try {
    const result = await createHandoff(pending.apiUrl, {
      token,
      tokenType,
      returnUrl: pending.returnUrl,
    });
    clearPendingSignIn();
    window.location.replace(
      buildPortalReturnUrl(result.returnUrl, { code: result.code }, pending.state),
    );
  } catch {
    backToPortal(pending, 'failed');
  }
};

/**
 * Neutral, unbranded page that is on screen only while the tab passes through on its way to the
 * provider and back to the portal. Many MSPs white-label their portal, so nothing here names us.
 */
const RedirectSignIn: React.FC = () => {
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  const start = async (request: IRedirectSignInRequest): Promise<void> => {
    // A new request replaces whatever an earlier, abandoned attempt left in this tab.
    clearPendingSignIn();

    // Never redirect anywhere an API hasn't confirmed as a portal callback, not even to report an error.
    const confirmed = await confirmReturnUrl(
      resolveClientPortalApiUrls(request.returnUrl, import.meta.env.VITE_CLIENT_PORTAL_API_URLS),
      request.returnUrl,
      validateReturnUrl,
    );
    if (confirmed === null) {
      setFailed(true);
      return;
    }

    const pending: IPendingRedirectSignIn = {
      type: request.type,
      apiUrl: confirmed.apiUrl,
      returnUrl: confirmed.returnUrl,
      state: request.state,
      googleNonce: request.type === 'Google' ? crypto.randomUUID() : undefined,
      startedAt: Date.now(),
    };

    if (!savePendingSignIn(pending)) {
      backToPortal(pending, 'failed');
      return;
    }

    // Drop the portal's query string so a reload or Back doesn't start over.
    window.history.replaceState(null, '', signInRedirectUri(window.location.href));

    try {
      if (request.type === 'Microsoft') {
        (await createMsal()).loginRedirect({ scopes: ['User.Read'], loginHint: request.loginHint });
      } else {
        const { clientId } = await getGoogleSettings();
        window.location.assign(
          buildGoogleAuthorizeUrl(
            clientId,
            signInRedirectUri(window.location.href),
            pending.googleNonce ?? '',
          ),
        );
      }
    } catch {
      backToPortal(pending, 'failed');
    }
  };

  const finishMicrosoft = async (pending: IPendingRedirectSignIn): Promise<void> => {
    if (!window.location.hash) {
      // Back from the Microsoft page without finishing.
      backToPortal(pending, 'cancelled');
      return;
    }

    let msal: UserAgentApplication;
    try {
      msal = await createMsal();
    } catch {
      backToPortal(pending, 'failed');
      return;
    }

    const outcome = await new Promise<{ token?: string; error?: SignInErrorKind }>(resolve => {
      const timer = window.setTimeout(() => {
        resolve({ error: 'failed' });
      }, MICROSOFT_RESPONSE_TIMEOUT_MS);

      msal.handleRedirectCallback((authError, response) => {
        window.clearTimeout(timer);
        if (authError) {
          resolve({ error: toSignInErrorKind(authError.errorCode) });
          return;
        }
        const token = response?.idToken?.rawIdToken;
        resolve(token ? { token } : { error: 'failed' });
      });
    });

    if (outcome.token) {
      await handOff(pending, outcome.token, 'Microsoft');
    } else {
      backToPortal(pending, outcome.error ?? 'failed');
    }
  };

  const finishGoogle = async (pending: IPendingRedirectSignIn): Promise<void> => {
    const { accessToken, state, error } = parseGoogleRedirect(window.location.hash);

    if (!accessToken && !error) {
      // Back from the Google page without finishing.
      backToPortal(pending, 'cancelled');
      return;
    }

    if (!pending.googleNonce || state !== pending.googleNonce) {
      // Not the response to the request this tab made.
      backToPortal(pending, 'failed');
      return;
    }

    if (error || !accessToken) {
      backToPortal(pending, toSignInErrorKind(error));
      return;
    }

    await handOff(pending, accessToken, 'Google');
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const run = async (): Promise<void> => {
      const request = parseRedirectSignInRequest(window.location.search);
      if (request) {
        await start(request);
        return;
      }

      const pending = readPendingSignIn();
      if (!pending) {
        setFailed(true);
        return;
      }

      if (pending.type === 'Google') {
        await finishGoogle(pending);
      } else {
        await finishMicrosoft(pending);
      }
    };

    run().catch(() => {
      setFailed(true);
    });
  }, []);

  return (
    <div className="redirect-sign-in" aria-live="polite">
      {failed ? (
        <div className="redirect-sign-in__failure">
          <p className="redirect-sign-in__message">
            We couldn&apos;t complete sign-in. Please go back to your portal and try again.
          </p>
          {/* The portal navigated here in the same tab, so its page is one step back in history.
              An unconfirmed address is never navigated to directly. */}
          <button
            type="button"
            className="redirect-sign-in__back"
            onClick={() => {
              window.history.back();
            }}
          >
            Go back
          </button>
        </div>
      ) : (
        <div className="redirect-sign-in__spinner" role="status" aria-label="Signing in" />
      )}
    </div>
  );
};

export default RedirectSignIn;
